import { Avatar, Button, Group, Modal, Stack, Switch, Text } from '@mantine/core';
import { RoleSegControl, RoleSection, SelectedRoles, ModalCloseButton } from './roleEditorShared';
import { getInitials } from '../../../lib/initials';
import {
  type RoleOption,
  groupRoles,
  withBase,
  productLevel,
  setProductLevel,
  roleLevel,
  BASE_ADMIN,
  BASE_MEMBER,
  MODAL_STYLES,
} from './roleEditorLogic';

interface RoleEditorUser {
  email: string;
  name: string;
  roles: string[];
}

interface RoleEditorModalProps {
  user: RoleEditorUser | null;
  available: RoleOption[];
  selected: string[];
  onSelectedChange: (next: string[]) => void;
  onClose: () => void;
  onSave: (email: string, roles: string[]) => void;
  error?: string | null;
  ownedProductKeys: string[];
}


export function RoleEditorModal({ user, available, selected, onSelectedChange, onClose, onSave, error, ownedProductKeys }: Readonly<RoleEditorModalProps>) {
  const { products } = groupRoles(available);
  const ownedProducts = products.filter(product => ownedProductKeys.includes(product.key));
  const orgAdmin = selected.includes(BASE_ADMIN);

  return (
    <Modal
      opened={user !== null}
      onClose={onClose}
      centered
      withCloseButton={false}
      size={540}
      radius="lg"
      padding={0}
      styles={MODAL_STYLES}
    >
      {user && (
        <>
          {/* Header */}
          <Group
            px={22}
            py={20}
            style={{ borderBottom: '1px solid var(--border-subtle)' }}
            wrap="nowrap"
            align="flex-start"
          >
            <Avatar
              size={42}
              radius="xl"
              color="white"
              style={{
                '--avatar-bg': 'linear-gradient(135deg, rgb(81, 100, 112), rgb(13, 40, 57))',
                '--avatar-bd': 'none',
                flexShrink: 0,
              }}
            >
              {getInitials(user.name)}
            </Avatar>
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Text fz="md" fw={700} lh="normal">{user.name}</Text>
              <Text fz="xs" c="var(--text-muted)" lh="normal">{user.email}</Text>
            </Stack>
            <ModalCloseButton onClick={onClose} />
          </Group>

          {/* Body */}
          <Stack px={22} pt={6} pb={18} gap={0} style={{ overflowY: 'auto', maxHeight: 'calc(88vh - 140px)' }}>
            <RoleSection
              title="Organisation administrator"
              desc="Full access to the Control Panel - users, roles, entitlements & tokens."
            >
              <Switch
                checked={orgAdmin}
                withThumbIndicator={false}
                style={{ '--mantine-color-dark-5': 'var(--border-strong)' }}
                onChange={e => onSelectedChange(withBase(selected, e.target.checked ? BASE_ADMIN : BASE_MEMBER))}
                color="var(--brand-accent)"
                mt={2}
              />
            </RoleSection>

            {ownedProducts.map(product => (
              <RoleSection key={product.key} title={product.name} desc={product.tagline}>
                <RoleSegControl
                  options={['none', ...product.roles.map(r => roleLevel(product.key, r.slug))]}
                  value={productLevel(selected, product)}
                  onChange={lvl => onSelectedChange(setProductLevel(selected, product, lvl))}
                />
              </RoleSection>
            ))}

            <SelectedRoles selected={selected} available={available} />
          </Stack>

          {error && (
            <Text px={22} pb={12} fz="sm" c="var(--status-danger)">
              {error}
            </Text>
          )}

          {/* Footer */}
          <Group
            justify="flex-end"
            gap={10}
            px={22}
            py={16}
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            <Button
              variant="default"
              onClick={onClose}
              style={{
                background: 'var(--bg-surface)',
                '--button-color': 'var(--text-muted)',
                '--button-hover-color': 'var(--accent-text)',
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => onSave(user.email, selected)}>
              Save changes
            </Button>
          </Group>
        </>
      )}
    </Modal>
  );
}
